import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '../hooks/useQuery';
import { useAuth } from '../hooks/useAuth';
import { api, type DriverDef } from '../lib/api';
import { Card, CardHeader, Badge, Spinner, ActionButton, EmptyState } from '../components/ui';
import PlanBudgetGrid from '../components/fpa/PlanBudgetGrid';
import PlanDriversGrid from '../components/fpa/PlanDriversGrid';
import PlanPeopleGrid from '../components/fpa/PlanPeopleGrid';
import PlanContractsGrid from '../components/fpa/PlanContractsGrid';
import PlanReviewSection from '../components/fpa/PlanReviewSection';
import PlanFormulasGrid from '../components/fpa/PlanFormulasGrid';
import { TmBuild } from '../components/fpa/PlanTmBuild';
import { ModelTab } from '../components/fpa/PlanModelMap';
import { EditPlanModal, ClonePlanModal } from '../components/fpa/PlanMetaModals';

/**
 * The FP&A plan workspace — the single interface for a plan (spec §8). The
 * Budget Grid is the default surface; the other sections reuse the same
 * components the old tabbed page used, so there is one place to do everything.
 */

type Section = 'grid' | 'revenue' | 'people' | 'formulas' | 'drivers' | 'review' | 'settings';
const SECTIONS: Array<{ key: Section; label: string }> = [
  { key: 'grid', label: 'Budget Grid' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'people', label: 'People' },
  { key: 'formulas', label: 'Formulas' },
  { key: 'drivers', label: 'Drivers' },
  { key: 'review', label: 'Review' },
  { key: 'settings', label: 'Settings' },
];

const STATUS_VARIANT: Record<string, 'gray' | 'amber' | 'blue' | 'green'> = {
  draft: 'gray', submitted: 'amber', approved: 'blue', locked: 'green', superseded: 'gray', restated: 'gray',
};

export default function AdminFpaPlanWorkspacePage() {
  const { id = '', section } = useParams<{ id: string; section?: Section }>();
  const planId = id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const active: Section = (section as Section) ?? 'grid';
  const planQ = useQuery(() => api.fpa.plans.get(planId), [planId]);

  const canWrite = user?.fpa_role === 'analyst' || user?.fpa_role === 'approver';
  const isApprover = user?.fpa_role === 'approver';
  const [err, setErr] = useState<string | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showClone, setShowClone] = useState(false);

  if (planQ.isLoading) return <Spinner />;
  if (!planQ.data) return <EmptyState message="Plan not found" />;
  const plan = planQ.data.plan;
  const isDraft = plan.status === 'draft';
  const editable = isDraft && canWrite;
  const canRecompute = canWrite && !['locked', 'superseded', 'restated'].includes(plan.status);
  const canDelete = isApprover && ['draft', 'superseded', 'restated'].includes(plan.status);

  async function lifecycle(action: 'submit' | 'approve' | 'reject' | 'lock') {
    setErr(null);
    try { await api.fpa.plans.transition(planId, action); planQ.refetch(); } catch (e) { setErr((e as Error).message); }
  }
  async function restate() {
    const nm = window.prompt('Name for the restated plan (the locked plan becomes read-only history):', `${plan.name} (restated)`);
    if (!nm) return;
    try { const r = await api.fpa.plans.restate(planId, { name: nm }); navigate(`/admin/fpa/plans/${r.plan.id}`); } catch (e) { setErr((e as Error).message); }
  }
  async function supersede() {
    if (!window.confirm(`Supersede (retire) "${plan.name}"? It stays as read-only history and is no longer the current plan.`)) return;
    try { await api.fpa.plans.supersede(planId); planQ.refetch(); } catch (e) { setErr((e as Error).message); }
  }
  async function remove() {
    if (!window.confirm(`Delete plan "${plan.name}"? This removes all its inputs and outputs and cannot be undone.`)) return;
    try { await api.fpa.plans.remove(planId); navigate('/admin/fpa/plans'); } catch (e) { setErr((e as Error).message); }
  }
  async function reforecast() {
    const at = window.prompt('Reforecast as of which month? (YYYY-MM) — actuals ≤ this, forecast after:');
    if (!at) return;
    try { const { plan: p } = await api.fpa.plans.reforecast(planId, { actuals_through: at }); navigate(`/admin/fpa/plans/${p.id}`); } catch (e) { setErr((e as Error).message); }
  }

  return (
    <Card>
      <CardHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Link to="/admin/fpa/plans" style={{ fontSize: 11, color: 'var(--color-text-muted)', textDecoration: 'none' }}>← Plans</Link>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{plan.name}</span>
          <Badge variant="gray">{plan.kind}</Badge>
          <Badge variant={STATUS_VARIANT[plan.status] ?? 'gray'}>{plan.status}</Badge>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{plan.horizon_start.slice(0, 7)} → {plan.horizon_end.slice(0, 7)}</span>
          {plan.actuals_through && <Badge variant="blue">actuals ≤ {plan.actuals_through}</Badge>}
        </div>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <ActionButton onClick={() => api.fpa.exportPlan(planId, plan.name)}>Export CSV</ActionButton>
          {canWrite && <ActionButton onClick={() => setShowEdit(true)}>Edit</ActionButton>}
          {canWrite && <ActionButton onClick={() => setShowClone(true)}>Clone</ActionButton>}
          {canWrite && <ActionButton onClick={reforecast}>Reforecast</ActionButton>}
          {isDraft && canWrite && <ActionButton onClick={() => lifecycle('submit')}>Submit</ActionButton>}
          {plan.status === 'submitted' && canWrite && <ActionButton onClick={() => lifecycle('reject')}>Reject</ActionButton>}
          {plan.status === 'submitted' && isApprover && <ActionButton variant="primary" onClick={() => lifecycle('approve')}>Approve</ActionButton>}
          {plan.status === 'approved' && isApprover && <ActionButton variant="primary" onClick={() => lifecycle('lock')}>Lock</ActionButton>}
          {plan.status === 'locked' && isApprover && <ActionButton variant="primary" onClick={restate}>Restate</ActionButton>}
          {(plan.status === 'approved' || plan.status === 'locked') && isApprover && <ActionButton onClick={supersede}>Supersede</ActionButton>}
          {canDelete && <ActionButton variant="danger" onClick={remove}>Delete</ActionButton>}
        </span>
      </CardHeader>

      {err && <div style={{ padding: '8px 16px', fontSize: 12, color: 'var(--color-danger, #c0392b)' }}>{err}</div>}

      <div style={{ display: 'flex', gap: 2, padding: '4px 12px', borderBottom: '0.5px solid var(--color-border)', flexWrap: 'wrap' }}>
        {SECTIONS.map((s) => (
          <button key={s.key} onClick={() => navigate(`/admin/fpa/plans/${planId}/w/${s.key}`)}
            style={{
              fontSize: 12, fontWeight: 500, padding: '5px 12px', border: 'none', cursor: 'pointer', borderRadius: 'var(--radius-sm)',
              background: active === s.key ? 'var(--color-blue)' : 'transparent',
              color: active === s.key ? '#fff' : 'var(--color-text)',
            }}>{s.label}</button>
        ))}
      </div>

      {showEdit && <EditPlanModal planId={planId} plan={plan} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); planQ.refetch(); }} />}
      {showClone && <ClonePlanModal planId={planId} plan={plan} onClose={() => setShowClone(false)} onCloned={(nid) => { setShowClone(false); navigate(`/admin/fpa/plans/${nid}`); }} />}

      {active === 'grid' && <PlanBudgetGrid plan={plan} canWrite={editable} onChanged={planQ.refetch} />}
      {active === 'revenue' && <div style={{ padding: '4px 0' }}><RevenuePanel planId={planId} canWrite={editable} /><PlanContractsGrid plan={plan} canWrite={editable} /><TmBuild planId={planId} /></div>}
      {active === 'people' && <PlanPeopleGrid plan={plan} canWrite={editable} />}
      {active === 'formulas' && <PlanFormulasGrid plan={plan} canWrite={editable} />}
      {active === 'drivers' && <div style={{ padding: '4px 0' }}><DriversPanel canWrite={canWrite} /><PlanDriversGrid plan={plan} canWrite={editable} /></div>}
      {active === 'review' && <div style={{ padding: '4px 0' }}><PlanReviewSection plan={plan} canRecompute={!!canRecompute} onRecomputed={planQ.refetch} /><ModelTab planId={planId} canWrite={canWrite} /></div>}
      {active === 'settings' && <SettingsPanel planId={planId} plan={plan} canWrite={editable} onChanged={planQ.refetch} />}
    </Card>
  );
}

function SettingsPanel({ planId, plan, canWrite, onChanged }: { planId: string; plan: { actuals_through?: string | null }; canWrite: boolean; onChanged: () => void }) {
  const [at, setAt] = useState(plan.actuals_through ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function apply() {
    setBusy(true); setErr(null); setMsg(null);
    try { await api.fpa.plans.setActualsThrough(planId, at.trim() || null); setMsg('Saved & recomputed.'); onChanged(); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <div style={{ padding: 16, fontSize: 12, maxWidth: 460 }}>
      <div style={{ fontWeight: 500, marginBottom: 8 }}>Rolling-forecast boundary</div>
      <p style={{ color: 'var(--color-text-muted)', marginTop: 0 }}>
        Set <code>actuals_through</code> (YYYY-MM) to blend QBO actuals for months at or before it and forecast the rest.
        Leave blank for a pure plan.
      </p>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input value={at} onChange={(e) => setAt(e.target.value)} placeholder="YYYY-MM" disabled={!canWrite}
          style={{ fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)', width: 100 }} />
        {canWrite && <ActionButton variant="primary" onClick={apply} disabled={busy}>Apply</ActionButton>}
      </div>
      {msg && <p style={{ color: 'var(--color-green)' }}>{msg}</p>}
      {err && <p style={{ color: 'var(--color-danger, #c0392b)' }}>{err}</p>}
    </div>
  );
}

function RevenuePanel({ planId, canWrite }: { planId: string; canWrite: boolean }) {
  const deptsQ = useQuery(() => api.fpa.departments(), [planId]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<'weighted' | 'committed'>('weighted');
  const [lag, setLag] = useState('0');
  const [ramp, setRamp] = useState<'point' | 'spread_3mo'>('point');
  const [mrr, setMrr] = useState<Record<string, string>>({});

  const departments = deptsQ.data?.departments ?? [];
  const box: React.CSSProperties = { fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)' };

  async function syncPipeline() {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.fpa.plans.syncPipeline(planId, { mode, close_lag_months: Number(lag) || 0, ramp });
      setMsg(`Pipeline synced: ${r.items} item(s) from ${r.deals_scheduled} deal(s).${r.warnings.length ? ' ' + r.warnings.join(' ') : ''}`);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  async function seedSubs() {
    const entries = Object.entries(mrr).map(([department_id, v]) => ({ department_id, opening_mrr: Number(v) })).filter((e) => Number.isFinite(e.opening_mrr) && e.opening_mrr !== 0);
    if (entries.length === 0) { setErr('Enter an opening MRR for at least one department.'); return; }
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.fpa.plans.seedSubscriptions(planId, entries);
      setMsg(`Subscriptions seeded: ${r.items} item(s).${r.warnings.length ? ' ⚠ ' + r.warnings.join(' ') : ''}`);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <div style={{ padding: 16, fontSize: 12, borderBottom: '0.5px solid var(--color-border)' }}>
      {err && <p style={{ color: 'var(--color-danger, #c0392b)' }}>{err}</p>}
      {msg && <p style={{ color: 'var(--color-green)' }}>{msg}</p>}
      <div style={{ fontWeight: 500, marginBottom: 6 }}>Pipeline (HubSpot)</div>
      <p style={{ color: 'var(--color-text-muted)', marginTop: 0 }}>Weights open deals by stage probability, phases them at close date + lag, groups per department (deal owner → FP&amp;A dept), and snapshots the result onto <code>rev.pipeline</code>.</p>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <select value={mode} onChange={(e) => setMode(e.target.value as 'weighted' | 'committed')} style={box}><option value="weighted">weighted</option><option value="committed">committed</option></select>
        <label style={{ color: 'var(--color-text-muted)' }}>lag <input value={lag} onChange={(e) => setLag(e.target.value)} style={{ ...box, width: 44 }} /> mo</label>
        <select value={ramp} onChange={(e) => setRamp(e.target.value as 'point' | 'spread_3mo')} style={box}><option value="point">point</option><option value="spread_3mo">spread 3mo</option></select>
        {canWrite && <ActionButton variant="primary" onClick={syncPipeline} disabled={busy}>Sync pipeline</ActionButton>}
      </div>
      <div style={{ fontWeight: 500, marginBottom: 6 }}>Subscriptions (opening MRR)</div>
      <p style={{ color: 'var(--color-text-muted)', marginTop: 0 }}>Seeds one <code>engine_subscription</code> item per department on <code>rev.sub</code>; the <code>sub.churn_pct_monthly</code> / <code>sub.expansion_pct_monthly</code> drivers compound it forward.</p>
      {departments.map((d) => (
        <div key={d.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
          <span style={{ width: 160 }}>{d.name}</span>
          <input placeholder="opening MRR" value={mrr[d.id] ?? ''} onChange={(e) => setMrr({ ...mrr, [d.id]: e.target.value })} style={{ ...box, width: 120 }} disabled={!canWrite} />
        </div>
      ))}
      {canWrite && <div style={{ marginTop: 8 }}><ActionButton variant="primary" onClick={seedSubs} disabled={busy}>Seed subscriptions</ActionButton></div>}
    </div>
  );
}

function DriversPanel({ canWrite }: { canWrite: boolean }) {
  const driversQ = useQuery(() => api.fpa.driverRegistry(), []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState<{ key: string; label: string; unit: string; scope: string } | null>(null);

  if (driversQ.isLoading) return <Spinner />;
  const drivers = driversQ.data?.drivers ?? [];
  const groups = new Map<string, DriverDef[]>();
  for (const d of drivers) (groups.get(d.group) ?? groups.set(d.group, []).get(d.group)!).push(d);

  async function createDriver() {
    if (!form) return;
    setBusy(true); setErr(null);
    try { await api.fpa.createDriver({ key: form.key.trim(), label: form.label.trim(), unit: form.unit, scope: form.scope }); setForm(null); driversQ.refetch(); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  async function toggleActive(d: DriverDef) {
    setBusy(true); setErr(null);
    try { await api.fpa.updateDriver(d.key, { is_active: !(d.is_active ?? true) }); driversQ.refetch(); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  async function removeDriver(key: string) {
    if (!window.confirm(`Delete custom driver ${key}?`)) return;
    setBusy(true); setErr(null);
    try { await api.fpa.deleteDriver(key); driversQ.refetch(); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  const box: React.CSSProperties = { fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)' };

  return (
    <div style={{ padding: 16, fontSize: 12, borderBottom: '0.5px solid var(--color-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 500 }}>Driver registry</div>
        {canWrite && <span style={{ marginLeft: 'auto' }}><ActionButton variant="primary" onClick={() => setForm({ key: '', label: '', unit: 'ratio', scope: 'department' })}>+ Custom driver</ActionButton></span>}
      </div>
      <p style={{ color: 'var(--color-text-muted)', marginTop: 0 }}>Driver <i>values</i> per month/department are edited below (Assumptions). This manages the registry.</p>
      {err && <p style={{ color: 'var(--color-danger, #c0392b)' }}>{err}</p>}
      {form && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
          <input placeholder="key (e.g. cost.hosting_pct)" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} style={{ ...box, width: 200 }} />
          <input placeholder="Label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} style={{ ...box, width: 160 }} />
          <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} style={box}>{['ratio', 'currency', 'rate', 'hours', 'months', 'count'].map((u) => <option key={u}>{u}</option>)}</select>
          <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} style={box}>{['department', 'company'].map((s) => <option key={s}>{s}</option>)}</select>
          <ActionButton variant="primary" onClick={createDriver} disabled={busy}>Create</ActionButton>
          <ActionButton onClick={() => setForm(null)}>Cancel</ActionButton>
        </div>
      )}
      {Array.from(groups.entries()).map(([grp, ds]) => (
        <div key={grp} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 4 }}>{grp}</div>
          {ds.map((d) => (
            <div key={d.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0', fontSize: 11 }}>
              <span style={{ fontFamily: 'monospace', width: 220 }}>{d.key}</span>
              <span style={{ width: 200 }}>{d.label}</span>
              <span style={{ color: 'var(--color-text-muted)' }}>{d.unit} · {d.scope}</span>
              {d.is_builtin ? <Badge variant="gray">built-in</Badge> : <Badge variant="blue">custom</Badge>}
              {(d.is_active ?? true) ? null : <Badge variant="amber">inactive</Badge>}
              {canWrite && !d.is_builtin && (
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <button onClick={() => toggleActive(d)} style={{ fontSize: 10, background: 'none', border: 'none', color: 'var(--color-blue)', cursor: 'pointer' }}>{(d.is_active ?? true) ? 'deactivate' : 'activate'}</button>
                  <button onClick={() => removeDriver(d.key)} style={{ fontSize: 10, background: 'none', border: 'none', color: 'var(--color-danger, #c0392b)', cursor: 'pointer' }}>delete</button>
                </span>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
